import {
  CartItem,
  ExtraElement,
  Order,
  OrderElement,
} from "interfaces/products";
import groupBy from "lodash/groupBy";
import { makeAutoObservable } from "mobx";
import { PromotionTypes, BusySlots } from "@prisma/client";

class Cart {
  isLoadCartFromLocalStorageCalled = false;
  updatingComboId: number | null = null;
  extras: Array<ExtraElement> = [];
  items: Array<CartItem> = [];
  busySlot: Array<BusySlots> | null = null;
  combos: Record<
    string,
    Array<{
      itemId: number;
      comboId: number;
      comboName: string;
      orderComboElementId?: number;
      comboElementId: number;
      group: number;
      productVariantId: number;
      name: string;
      quantity: number;
      amountAfterVat: number;
      promotionAmount?: number;
    }>
  > = {};

  customer: Partial<{
    id?: number;
    name: string;
    phoneNumber: string;
    email: string;
  }> = {};

  picksUpAt: Date | null = null;

  backendAmount: string | null = null;
  clientSecret: string | null = null;
  promotionType: PromotionTypes | null = null;
  promotion: number = 0;
  isPaid: boolean | null = null;
  orderType: "en magasin" | "en ligne" | null = null;
  paymentMethod:
    | "en ligne"
    | "espèce"
    | "carte bleue"
    | "multiple"
    | "chèque vacances"
    | "ticket restaurant"
    | "par virement bancaire"
    | "chèque"
    | null = null;
  publicToken?: string;
  orderId?: string;

  is100PointsPromotionActive: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  setItems(items: Array<CartItem>) {
    this.items = items;
    localStorage.setItem("items", JSON.stringify(this.items));
  }

  setElements(elements: Array<OrderElement>) {
    this.items = elements.map((e) => {
      return {
        ...e,
        comboId: e.orderComboElement?.comboElement?.comboId,
        name: e.variant.product.name,
        type: e.variant.product.type,
        productId: e.variant.product.id,
        comboElementId: e.orderComboElement?.comboElement?.id,
      };
    });

    let combos: Array<{
      itemId: number;
      comboId: number;
      comboName: string;
      orderComboElementId: number;
      comboElementId: number;
      group: number;
      productVariantId: number;
      name: string;
      quantity: number;
      amountAfterVat: number;
    }> = [];

    for (const item of this.items) {
      if (!item.orderComboElementId || !item.orderComboElement) continue;

      if (
        !item.orderComboElement?.comboElement?.comboId ||
        !item.orderComboElement.comboElement.id
      ) {
        continue;
      }

      combos.push({
        itemId: item.id,
        comboId: item.orderComboElement?.comboElement?.comboId,
        comboName: item.orderComboElement.comboElement.combo.name,
        orderComboElementId: item.orderComboElementId,
        productVariantId: item.variant.id,
        comboElementId: item.orderComboElement.comboElement.id,
        group: item.orderComboElement.group,
        name: `${item.name} ${item.variant.name}`,
        quantity: item.quantity,
        amountAfterVat: item.amountAfterVat,
      });
    }

    if (combos.length > 0) {
      this.combos = groupBy(combos, "group");
    }

    localStorage.setItem("items", JSON.stringify(this.items));
  }

  setExtras(extras: Array<ExtraElement>) {
    this.extras = extras;
    localStorage.setItem("extras", JSON.stringify(this.extras));
  }

  setUpdatingComboId(updatingComboId: number | null) {
    this.updatingComboId = updatingComboId;
  }

  addElement({
    itemId,
    variantId,
    variantName,
    productId,
    productName,
    productType,
    quantity,
    priceAfterVatAfterPromotion,
    priceAfterVatBeforePromotion,
    comboId,
    comboElementId,
    orderComboElement,
  }: {
    itemId: number;
    variantId: number;
    productId: number;
    variantName: string;
    productName: string;
    productType: { name: string };
    quantity: number;
    priceAfterVatAfterPromotion: number;
    priceAfterVatBeforePromotion: number;
    comboId?: number;
    comboElementId?: number;
    orderComboElement?: {
      comboId: number;
      priceAfterVat: number;
      group: number;
    };
  }) {
    const existingEntry = this.items.find(
      (e) => itemId === e.variant.id && !e.comboId
    );

    if (!existingEntry) {
      this.items = [
        ...this.items,
        {
          id: itemId,
          productId,
          name: productName,
          type: productType,
          amountAfterVat: Number(priceAfterVatAfterPromotion) * Number(quantity),
          variant: {
            id: variantId,
            name: variantName,
            priceAfterVat: Number(priceAfterVatBeforePromotion),
          },
          promotionAmount: 0,
          quantity: Number(quantity),
          comboId,
          comboElementId,
          orderComboElement,
        },
      ];

      localStorage.setItem("items", JSON.stringify(this.items));
      return;
    }

    let idx = 0;

    for (const item of this.items) {
      if (itemId === item.variant.id && !item.comboId) {
        this.items[idx].quantity = Number(this.items[idx].quantity) + Number(quantity);
        this.items[idx].amountAfterVat =
          Number(this.items[idx].amountAfterVat) +
          Number(priceAfterVatAfterPromotion) * Number(quantity);
        break;
      }

      idx++;
    }

    localStorage.setItem("items", JSON.stringify(this.items));
  }

  addCombo(
    comboElements: Array<{
      itemId: number;
      comboId: number;
      comboElementId: number;
      comboName: string;
      productVariantId: number;
      group: number;
      name: string;
      quantity: number;
      amountAfterVat: number;
    }>
  ) {
    this.combos[new Date().getTime()] = comboElements;

    localStorage.setItem("combos", JSON.stringify(this.combos));
  }

  removeCombo(id: number) {
    const { [id]: combo, ...rest } = this.combos;

    this.combos = rest;
    this.items = this.items.filter(
      (item) => !combo.some((e) => e.itemId === item.id)
    );

    localStorage.setItem("combos", JSON.stringify(this.combos));
    localStorage.setItem("items", JSON.stringify(this.items));
  }

  removeElement(itemId: number) {
    this.items = this.items.filter((e) => itemId !== e.id);

    localStorage.setItem("items", JSON.stringify(this.items));
  }

  reset() {
    this.items = [];
    this.extras = [];
    this.combos = {};
    this.promotion = 0;
    this.isPaid = false;
    this.orderId = null;
    this.publicToken = null;
    this.customer = {};
    this.picksUpAt = null;
    this.backendAmount = null;
    this.is100PointsPromotionActive = false;
    this.promotionType = null;
    this.busySlot = null;

    localStorage.setItem("promotionType", null);
    localStorage.setItem("items", JSON.stringify(this.items));
    localStorage.setItem("extras", JSON.stringify(this.extras));
    localStorage.setItem("combos", JSON.stringify(this.combos));
    localStorage.setItem("promotion", JSON.stringify(this.promotion));
    localStorage.setItem("orderId", JSON.stringify(this.orderId));
    localStorage.setItem("publicToken", JSON.stringify(this.publicToken));
    localStorage.setItem("customer", JSON.stringify(this.customer));
    localStorage.setItem("picksUpAt", null);
    localStorage.setItem("backendAmount", this.backendAmount);
    localStorage.setItem("is100PointsPromotionActive", "false");
    localStorage.setItem("busySlot", null);
  }

  setCustomerField(fieldName: "email" | "phoneNumber" | "name", value: string) {
    this.customer[fieldName] = value;

    localStorage.setItem("customer", JSON.stringify(this.customer));
  }

  setPicksUpAt(time: Date) {
    this.picksUpAt = time;

    localStorage.setItem("picksUpAt", this.picksUpAt.toISOString());
  }

  setClientSecret(clientSecret: string) {
    this.clientSecret = clientSecret;

    localStorage.setItem("clientSecret", this.clientSecret);
  }

  setBackendAmount(backendAmount: string) {
    this.backendAmount = backendAmount;

    localStorage.setItem("backendAmount", this.backendAmount);
  }

  setPromotion(promotion: number) {
    this.promotion = promotion;

    localStorage.setItem("promotion", String(this.promotion));
  }

  setIsPaid(isPaid: boolean) {
    this.isPaid = isPaid;
  }

  setPublicToken(publicToken: string) {
    this.publicToken = publicToken;

    localStorage.setItem("publicToken", this.publicToken);
  }

  setOrderId(orderId: string) {
    this.orderId = orderId;

    localStorage.setItem("orderId", this.orderId);
  }

  setIs100PointsPromotionActive(isActive: boolean) {
    this.is100PointsPromotionActive = isActive;
    localStorage.setItem("is100PointsPromotionActive", String(isActive));
  }

  setPromotionType(type: PromotionTypes | null) {
    this.promotionType = type;
    localStorage.setItem("promotionType", type);
  }

  get totalAfterVat() {
    return (
      this.items
        .filter((e) => !e.orderComboElement)
        .reduce(
          (acc, e) => acc + e.quantity * Number(e.variant.priceAfterVat),
          0
        ) +
      this.items
        .filter((e) => !!e.orderComboElement)
        .reduce(
          (acc, e) => acc + Number(e.orderComboElement?.priceAfterVat),
          0
        ) +
      this.extras.reduce(
        (acc, e) => acc + e.quantity * Number(e.extra.priceAfterVat),
        0
      )
    );
  }

  get totalPromotion() {
    const perArticlePromotion = this.items
      .filter((e) => !e.orderComboElement)
      .reduce(
        (acc, e) =>
          acc +
          e.quantity *
            Number(e.variant.priceAfterVat) *
            (e.promotionAmount / 100),
        0
      );

    const perComboArticlePromotion = this.items
      .filter((e) => !!e.orderComboElement)
      .reduce(
        (acc, e) =>
          acc +
          Number(e.orderComboElement?.priceAfterVat) *
            (e.promotionAmount / 100),
        0
      );

    const perExtraPromotion = this.extras.reduce((acc, e) => {
      return (
        acc +
        e.quantity *
          Number(e.extra.priceAfterVat) *
          ((e.promotionAmount || 0) / 100)
      );
    }, 0);

    // const priceWithoutPromotion =
    //   this.items
    //     .filter((e) => !e.orderComboElement)
    //     .reduce(
    //       (acc, e) => acc + e.quantity * Number(e.variant.priceAfterVat),
    //       0
    //     ) +
    //   this.items
    //     .filter((e) => !!e.orderComboElement)
    //     .reduce(
    //       (acc, e) => acc + Number(e.orderComboElement?.priceAfterVat),
    //       0
    //     ) +
    //   this.extras.reduce(
    //     (acc, e) => acc + e.quantity * Number(e.extra.priceAfterVat),
    //     0
    //   );

    return perArticlePromotion + perExtraPromotion + perComboArticlePromotion;
  }

  addExtra(extra: ExtraElement) {
    const existingEntry = this.extras.find(
      (e) =>
        e.parentOrderElementId === extra.parentOrderElementId &&
        e.extra.id === extra.extra.id
    );

    if (!existingEntry) {
      this.extras = [...this.extras, extra];
    } else {
      this.extras = this.extras.map((e) =>
        e.parentOrderElementId !== extra.parentOrderElementId ||
        e.extra.id !== extra.extra.id
          ? e
          : {
              ...e,
              quantity: e.quantity + extra.quantity,
            }
      );
    }

    localStorage.setItem("extras", JSON.stringify(this.extras));
  }

  removeExtra(extraId: number) {
    this.extras = this.extras.filter((e) => extraId !== e.id);

    localStorage.setItem("extras", JSON.stringify(this.extras));
  }

  setCart(order: Order & { busySlot: Array<BusySlots> }) {
    if (order.sellerOrder?.promotionType) {
      this.promotionType = order.sellerOrder.promotionType;
    } else if (
      order.customerOrder &&
      order.elements &&
      order.elements.length > 0 &&
      order.elements[0].promotionAmount > 0
    ) {
      // If it's a customer order with a promotion, it's always a global promotion
      this.promotionType = "global";
    }

    // Set busySlot from order
    this.busySlot = order.busySlot ? order.busySlot : null;

    if (order.orderPaymentMethods && order.orderPaymentMethods.length > 1) {
      this.paymentMethod = "multiple";
    }

    if (order.orderPaymentMethods?.length === 1) {
      if (order.orderPaymentMethods[0].paymentMethod.name === "cash") {
        this.paymentMethod = "espèce";
      }

      if (order.orderPaymentMethods[0].paymentMethod.name === "credit-card") {
        this.paymentMethod = "carte bleue";
      }

      if (order.orderPaymentMethods[0].paymentMethod.name === "check") {
        this.paymentMethod = "chèque";
      }

      if (order.orderPaymentMethods[0].paymentMethod.name === "holiday-check") {
        this.paymentMethod = "chèque vacances";
      }

      if (
        order.orderPaymentMethods[0].paymentMethod.name === "restaurant-check"
      ) {
        this.paymentMethod = "ticket restaurant";
      }

      if (order.orderPaymentMethods[0].paymentMethod.name === "online") {
        this.paymentMethod = "en ligne";
      }

      if (order.orderPaymentMethods[0].paymentMethod.name === "bank-transfer") {
        this.paymentMethod = "par virement bancaire";
      }
    }

    if (order.customer) {
      this.customer = order.customer;
    }

    if (order.customerOrder) this.orderType = "en ligne";

    if (order.sellerOrder) this.orderType = "en magasin";

    if (!order.customerOrder && !order.sellerOrder) this.orderType = null;

    this.orderId = String(order.id);

    this.picksUpAt = new Date(order.picksUpAt);

    if (order.elements) {
      this.items = order.elements.map((e) => {
        return {
          ...e,
          comboId: e.orderComboElement?.comboElement?.comboId,
          name: e.variant.product.name,
          type: e.variant.product.type,
          productId: e.variant.product.id,
          comboElementId: e.orderComboElement?.comboElement?.id,
        };
      });

      let combos: Array<{
        itemId: number;
        comboId: number;
        comboName: string;
        orderComboElementId: number;
        comboElementId: number;
        group: number;
        productVariantId: number;
        name: string;
        quantity: number;
        amountAfterVat: number;
        promotionAmount?: number;
      }> = [];

      for (const item of this.items) {
        if (!item.orderComboElementId || !item.orderComboElement) continue;

        if (
          !item.orderComboElement?.comboElement?.comboId ||
          !item.orderComboElement.comboElement.id
        ) {
          continue;
        }

        combos.push({
          itemId: item.id,
          comboId: item.orderComboElement?.comboElement?.comboId,
          comboName: item.orderComboElement.comboElement.combo.name,
          orderComboElementId: item.orderComboElementId,
          productVariantId: item.variant.id,
          comboElementId: item.orderComboElement.comboElement.id,
          group: item.orderComboElement.group,
          name: `${item.name} ${item.variant.name}`,
          quantity: item.quantity,
          amountAfterVat: item.amountAfterVat,
          promotionAmount: item.promotionAmount,
        });
      }

      if (combos.length > 0) {
        this.combos = groupBy(combos, "group");
      }
    }

    if (order.extraElements) this.extras = order.extraElements;

    this.backendAmount = Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
    }).format(order.amountAfterVat);
  }

  loadCartFromLocalStorage() {
    if (typeof localStorage === "undefined") return;

    const customer = localStorage.getItem("customer");
    const picksUpAt = localStorage.getItem("picksUpAt");
    const items = localStorage.getItem("items");
    const extras = localStorage.getItem("extras");
    const backendAmount = localStorage.getItem("backendAmount");
    const promotion = localStorage.getItem("promotion");
    const clientSecret = localStorage.getItem("clientSecret");
    const combos = localStorage.getItem("combos");
    const publicToken = localStorage.getItem("publicToken");
    const orderId = localStorage.getItem("orderId");
    const is100PointsPromotionActive = localStorage.getItem(
      "is100PointsPromotionActive"
    );
    const promotionType = localStorage.getItem("promotionType");
    const busySlot = localStorage.getItem("busySlot");

    this.customer = customer ? JSON.parse(customer) : {};
    this.combos = combos ? JSON.parse(combos) : {};
    this.picksUpAt =
      picksUpAt && picksUpAt !== "null" ? new Date(picksUpAt) : null;
    this.items = items ? JSON.parse(items) : [];
    this.extras = extras ? JSON.parse(extras) : [];
    this.backendAmount = backendAmount;
    this.promotion = Number(promotion);
    this.clientSecret = clientSecret;
    this.publicToken = publicToken;
    this.orderId = orderId;
    this.is100PointsPromotionActive = is100PointsPromotionActive === "true";
    this.promotionType = promotionType as PromotionTypes | null;
    this.busySlot = busySlot ? JSON.parse(busySlot) : null;
    this.isLoadCartFromLocalStorageCalled = true;
  }
}

const cart = new Cart();

export default cart;

if (global.window) {
  // @ts-ignore
  global.window.cart = cart;
}
